import React from "react";
import {
  Box,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";

function Step1({ control }) {
  const { setValue, getValues } = useFormContext();

  // On mount: if street has a value but address is empty, mirror it
  React.useEffect(() => {
    const street = getValues("step_one.invoice.street");
    const address = getValues("step_one.invoice.address");
    if (street && !address) {
      setValue("step_one.invoice.address", street, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, []);

  return (
    <Box>
      {/* Card: Neuen Kunden erstellen */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, paddingRight:"30%" }}>
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
          Neuen Kunden erstellen
        </Typography>

        <Grid container spacing={4}>
          {/* Kundenname */}
          <Grid item xs={12} md={6}>
            <Controller
              name="step_one.customerName"
              control={control}
              rules={{ required: "Pflichtfeld" }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Kundenname (Unternehmen)"
                  placeholder="Kundenname (Unternehmen)"
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message || 'Anzeigename, z. B.: "AUDI"'
                  }
                />
              )}
            />
          </Grid>

          {/* Kundenstatus */}
          <Grid item xs={12} md={6}>
            <Controller
              name="step_one.status"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Kundenstatus"
                >
                  <MenuItem value="aktiviert">aktiviert</MenuItem>
                  <MenuItem value="deaktiviert">deaktiviert</MenuItem>
                  <MenuItem value="in_planung">in Planung</MenuItem>
                  <MenuItem value="offline">offline</MenuItem>
                </TextField>
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Card: Rechnungsadresse */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2,paddingRight:"30%"  }}>
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
          Rechnungsadresse
        </Typography>

        <Grid container spacing={4}>
          {/* Unternehmen */}
          <Grid item xs={12} md={6}>
            <Controller
              name="step_one.invoice.company"
              control={control}
              rules={{ required: "Pflichtfeld" }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Unternehmen"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>

          {/* Adresszusatz */}
          <Grid item xs={12} md={6}>
            <Controller
              name="step_one.invoice.addressLine2"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Adresszusatz (optional)"
                  fullWidth
                />
              )}
            />
          </Grid>

          {/* Straße und Hausnummer (visible) -> syncs to street under the hood */}
          <Grid item xs={12} md={6}>
            <Controller
              name="step_one.invoice.address"
              control={control}
              rules={{ required: "Pflichtfeld" }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Straße und Hausnummer"
                  placeholder="Straße und Hausnummer"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  onChange={(e) => {
                    field.onChange(e); // update address
                    // mirror into 'street' for backward compatibility
                    setValue("step_one.invoice.street", e.target.value, {
                      shouldDirty: true,
                      shouldValidate: false,
                    });
                  }}
                />
              )}
            />
          </Grid>

          {/* Land */}
          <Grid item xs={12} md={6}>
            <Controller
              name="step_one.invoice.country"
              control={control}
              rules={{ required: "Pflichtfeld" }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  label="Land"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                >
                  {["Deutschland", "Österreich", "Schweiz"].map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          {/* PLZ */}
          <Grid item xs={12} md={3}>
            <Controller
              name="step_one.invoice.zip"
              control={control}
              rules={{
                required: "Pflichtfeld",
                pattern: {
                  value: /^[0-9A-Za-z -]{3,10}$/,
                  message: "Ungültige PLZ",
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="PLZ"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>

          {/* Ort */}
          <Grid item xs={12} md={3}>
            <Controller
              name="step_one.invoice.city"
              control={control}
              rules={{ required: "Pflichtfeld" }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Ort"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>

          {/* E-Mail */}
          <Grid item xs={12} md={6}>
            <Controller
              name="step_one.invoice.email"
              control={control}
              rules={{
                required: "Pflichtfeld",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Ungültige E-Mail-Adresse",
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="E-Mail-Adresse"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Zusatzinfos */}
          <Grid item xs={12}>
            <Controller
              name="step_one.invoice.extraInfo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Rechnungsinformationen (optional)"
                  placeholder="z. B.: Bestellnummer, etc."
                  fullWidth
                  multiline
                  helperText="z.B.: Bestellnummer, etc."
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default Step1;
